import {
  checkIsSubCompanyManagedSource,
  checkIsTpoSeededSource,
  PipelineExposureScopeType,
} from 'app/services/api/subCompanyPipelineAccessApi.constants';

import { getSecurityGroupDisplayName } from 'utils/securityGroups';

// Map ceiling-derivation source channels (the broker-safe channel-name source) to a channelId ->
// name lookup. Never call the Pricing channel catalog directly from the broker — it 403s.
export const buildChannelNameById = sourceChannels =>
  (sourceChannels || []).reduce((acc, channel) => {
    if (channel?.channelId) {
      acc[channel.channelId] = channel.channelName;
    }

    return acc;
  }, {});

// A SpecificChannels exposure resolves to the names of its effective channels; an All-scope
// exposure renders as "All channels" (empty list). Unresolved names (a degraded derivation) drop
// out — the label falls back to the scope-aware "Specific channels" caption downstream.
const resolveChannelNames = (pipeline, channelNameById) => {
  if (pipeline?.scopeType !== PipelineExposureScopeType.SPECIFIC_CHANNELS) {
    return [];
  }

  return (pipeline.effectiveChannelIds || [])
    .map(channelId => channelNameById[channelId])
    .filter(Boolean);
};

// One attachment row -> the cell shape the presentation layer reads (isDefault/isLocked/isRevoked/
// isEnabled). A TpoSeeded row is enabled unless the broker tombstoned it; a SubCompanyManaged grant
// is always enabled (broker grants are hard-deleted when turned off, never tombstoned).
const toCell = attachment => {
  const isDefault = checkIsTpoSeededSource(attachment.source);

  return {
    roleId: attachment.securityGroupId,
    isDefault,
    isLocked: !!attachment.isLocked,
    isRevoked: !!attachment.isRevoked,
    isEnabled: isDefault
      ? !attachment.isRevoked
      : checkIsSubCompanyManagedSource(attachment.source),
  };
};

// A pipeline is "at defaults" when the broker has neither added a grant nor hidden a seeded default
// on it — exactly the condition the reset action clears.
const checkIsAtDefaults = attachments =>
  !(attachments || []).some(
    attachment => checkIsSubCompanyManagedSource(attachment.source) || attachment.isRevoked,
  );

// Compose the flat server responses into the { roles, pipelines } matrix the tab renders. Rows come
// from the SkynetAPI exposed-pipelines BFF, cells from the per-pipeline Loans securityGroups reads,
// role columns from the sub-company allowed-roles source, channel names from ceiling-derivation.
export const composePipelineAccessMatrix = ({
  allowedRoles = [],
  exposedPipelines = [],
  securityGroupsByPipelineId = {},
  channelNameById = {},
}) => ({
  roles: allowedRoles.map(role => ({
    roleId: role.roleId,
    name: getSecurityGroupDisplayName(role.roleName, role.isSeededFromTemplate),
  })),
  pipelines: exposedPipelines.map(pipeline => {
    const attachments = securityGroupsByPipelineId[pipeline.pipelineId] || [];

    return {
      pipelineId: pipeline.pipelineId,
      name: pipeline.pipelineName,
      scopeType: pipeline.scopeType,
      channelNames: resolveChannelNames(pipeline, channelNameById),
      isAtDefaults: checkIsAtDefaults(attachments),
      cells: attachments.map(toCell),
    };
  }),
});
